import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export type SliderItem = {
  url: string;
  alt?: string;
  type: 'video' | 'image';
};

type SparkUISliderProps = {
  sliderItems: SliderItem[];
  width?: number;
  height?: number;
  initialIndex?: number;
};

const navStyles = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'white',
  color: 'black',
  zIndex: 1,
  minWidth: '40px',
  minHeight: '40px',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    outline: '1px solid white',
  },
};

const SparkUISlider = ({
  sliderItems,
  width = 900,
  height = 600,
  initialIndex = 0,
}: SparkUISliderProps) => {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const [isVideoLoading, setIsVideoLoading] = React.useState(
    sliderItems[initialIndex].type === 'video'
  );
  const videoRef = React.useRef<HTMLIFrameElement | null>(null);

  React.useEffect(() => {
    if (sliderItems[currentIndex].type === 'video') {
      if (videoRef.current) {
        videoRef.current.src = sliderItems[currentIndex].url;
      }
    } else {
      if (videoRef.current) {
        videoRef.current.src = '';
      }
    }
  }, [currentIndex, sliderItems]);

  const totalSlides = sliderItems.length;

  const handleNextSlide = () => {
    setIsVideoLoading(true);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides);
  };

  const handleVideoLoad = () => {
    setIsVideoLoading(false);
  };

  const handlePrevSlide = () => {
    setIsVideoLoading(true);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? totalSlides - 1 : prevIndex - 1
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box position='relative'>
        {sliderItems.length > 1 && (
          <>
            <Button
              onClick={handlePrevSlide}
              sx={{
                left: '-80px',
                ...navStyles,
              }}
            >
              <ArrowBackIcon />
            </Button>
            <Button
              onClick={handleNextSlide}
              sx={{
                right: '-80px',
                ...navStyles,
              }}
            >
              <ArrowForwardIcon />
            </Button>
          </>
        )}

        <Box
          sx={{
            display: 'flex',
            width: `${width}px`,
            height: `${height}px`,
            justifyContent: 'center',
          }}
        >
          {sliderItems.map((item, idx) => {
            if (item.type === 'video') {
              return (
                <iframe
                  key={idx}
                  ref={videoRef}
                  width='100%'
                  height='100%'
                  title='Video Player'
                  allow='autoplay; fullscreen'
                  style={{
                    objectFit: 'cover',
                    display: currentIndex === idx ? 'block' : 'none',
                    position: 'relative',
                    borderColor: 'transparent',
                  }}
                  referrerPolicy='strict-origin-when-cross-origin'
                  allowFullScreen
                  onLoad={handleVideoLoad}
                />
              );
            }

            if (item.type === 'image') {
              return (
                <Box
                  key={idx}
                  component='img'
                  src={item.url}
                  alt={item.alt}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: currentIndex === idx ? 'block' : 'none',
                  }}
                />
              );
            }

            return null;
          })}

          {isVideoLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 2,
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            left: '50%',
            gap: 2,
            bottom: '-30px',
            transform: 'translateX(-50%)',
          }}
        >
          {sliderItems.map((_, index) => (
            <Box
              key={index}
              sx={{
                background: currentIndex === index ? 'white' : 'gray',
                width: 10,
                height: 10,
                borderRadius: '50%',
                cursor: 'pointer',
              }}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SparkUISlider;
